<script setup lang="ts">
/**
 * Le journal d'événements d'une alarme (S:7761-7765, S:7792-7793).
 *
 * Ce que l'utilisateur doit vivre : il lit, dans l'ordre du temps, ce qui est
 * arrivé à l'alarme et pourquoi un message n'est pas parti, chaque ligne
 * numérotée comme son marqueur sur la courbe. La correspondance ne repose
 * jamais sur la seule couleur : c'est le numéro.
 */
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import type { LigneLue } from "@modules/alarmes-v2/utils/journal"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
withDefaults(
    defineProps<{
        /** Les lignes, déjà dites et numérotées (`lireLeJournal`). */
        lignes: LigneLue[]
        /** Vrai quand le journal a d'autres lignes, non lues. */
        incomplet?: boolean
    }>(),
    { incomplet: false }
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section class="flex flex-col gap-2" data-test="journal">
        <h2 class="text-sm font-semibold text-surface-900">
            {{ t("alarmesV2.detail.journal") }}
        </h2>
        <p class="text-xs text-surface-600">
            {{ t("alarmesV2.detail.journalAide") }}
        </p>
        <p
            v-if="lignes.length === 0"
            class="text-sm text-surface-600"
            data-test="journal-vide"
        >
            {{ t("alarmesV2.detail.journalVide") }}
        </p>
        <ol v-else class="flex flex-col gap-2">
            <li
                v-for="l in lignes"
                :key="l.num"
                class="flex gap-3 text-sm"
                data-test="ligne-journal"
            >
                <span
                    class="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-primary-700 text-[0.65rem] font-bold text-primary-700"
                    aria-hidden="true"
                    >{{ l.num }}</span
                >
                <span class="w-36 shrink-0 text-surface-600">{{
                    l.quand
                }}</span>
                <span class="flex flex-col">
                    <span class="font-medium text-surface-900">{{
                        l.titre
                    }}</span>
                    <span v-if="l.texte" class="text-surface-700">{{
                        l.texte
                    }}</span>
                </span>
            </li>
        </ol>
        <p
            v-if="incomplet"
            class="text-xs text-surface-600"
            data-test="journal-incomplet"
        >
            {{ t("alarmesV2.detail.journalIncomplet") }}
        </p>
    </section>
</template>
